import { z } from 'zod'
import {
  CONTEXT_SCOPES,
  contextPatchSchema,
  ownContextLayer,
  parentContextLayers,
  patchContext,
  relationshipSchema,
  sourceMappingSchema,
  structuredContextSchema,
  type ContextLayer,
  type ContextScope,
  type Relationship,
  type StructuredContext,
} from './context.js'
import { DomainError } from './errors.js'
import { newId } from './ids.js'
import type { NormalizedBoard } from './normalized.js'

/**
 * Findings: what an agent observed in the implementation, kept apart from intended
 * architecture. A finding never changes intent by itself. When the observation fits the
 * structured context fields it carries a proposal in exactly the shape `update_context`
 * takes; a person accepts it, which applies it as an ordinary edit, or dismisses it.
 */

export const FINDING_KINDS = ['drift', 'missing_component', 'violation', 'note'] as const
export type FindingKind = (typeof FINDING_KINDS)[number]

export const FINDING_KIND_LABELS: Record<FindingKind, string> = {
  drift: 'Drift',
  missing_component: 'Missing component',
  violation: 'Rule violation',
  note: 'Note',
}

export const FINDING_STATUSES = ['open', 'accepted', 'dismissed'] as const
export type FindingStatus = (typeof FINDING_STATUSES)[number]

export const FINDING_LIMITS = {
  summary: 200,
  detail: 4_000,
  evidence: 20,
  relationships: 20,
  reporter: 200,
} as const

const trimmed = (max: number) =>
  z
    .string()
    .transform((s) => s.trim())
    .pipe(z.string().max(max, `Maximum ${max} characters`))

/** A relationship a finding proposes from its target card. It gets an id when accepted. */
export const proposedRelationshipSchema = relationshipSchema.omit({ id: true })
export type ProposedRelationship = z.infer<typeof proposedRelationshipSchema>

export const findingProposalSchema = z
  .object({
    /** A change to the target's own context, as `update_context` takes it. */
    context: contextPatchSchema.optional(),
    /** Relationships to add from the target card. Only for a card target. */
    addRelationships: z.array(proposedRelationshipSchema).max(FINDING_LIMITS.relationships).optional(),
  })
  .strict()
export type FindingProposal = z.infer<typeof findingProposalSchema>

export const findingSchema = z.object({
  id: z.uuid(),
  boardId: z.uuid(),
  /** What the finding is about. For the board, targetId is the board id. */
  targetScope: z.enum(CONTEXT_SCOPES),
  targetId: z.uuid(),
  kind: z.enum(FINDING_KINDS),
  summary: trimmed(FINDING_LIMITS.summary).pipe(z.string().min(1, 'A summary is required')),
  detail: trimmed(FINDING_LIMITS.detail).default(''),
  /** Where in the code the observation was made. */
  evidence: z.array(sourceMappingSchema).max(FINDING_LIMITS.evidence).default([]),
  proposal: findingProposalSchema.nullable().default(null),
  status: z.enum(FINDING_STATUSES).default('open'),
  /** The integration that reported it, set by the server that received it. */
  reporter: trimmed(FINDING_LIMITS.reporter).default(''),
  createdAt: z.iso.datetime({ offset: true }),
  resolvedAt: z.iso.datetime({ offset: true }).nullable().default(null),
})

export type Finding = z.infer<typeof findingSchema>

/** What a finding.create operation carries. Status and timestamps are the applier's. */
export const findingCreateSchema = findingSchema.omit({ status: true, createdAt: true, resolvedAt: true })
export type FindingCreate = z.infer<typeof findingCreateSchema>

export function findingHasProposal(finding: Pick<Finding, 'proposal'>): boolean {
  const proposal = finding.proposal
  if (!proposal) return false
  const context = proposal.context
  const contextParts = context ? Object.values(context).some((part) => part !== undefined) : false
  return contextParts || (proposal.addRelationships?.length ?? 0) > 0
}

// --- target and proposal validation ----------------------------------------------

interface TargetState {
  own: ContextLayer
  parents: ContextLayer[]
  title: string
  relationships: Relationship[] | null
}

function targetState(state: NormalizedBoard, scope: ContextScope, targetId: string): TargetState {
  if (scope === 'board') {
    if (targetId !== state.board.id) throw new DomainError('invalid_reference', 'A board finding must target this board')
    return { own: ownContextLayer(state, { scope: 'board' })!, parents: [], title: state.board.name, relationships: null }
  }
  if (scope === 'card') {
    const card = state.cards[targetId]
    if (!card) throw new DomainError('invalid_reference', 'The finding’s target card is not on this board')
    return {
      own: ownContextLayer(state, { scope: 'card', id: targetId })!,
      parents: parentContextLayers(state, { scope: 'card', rowId: card.rowId, columnId: card.columnId }),
      title: card.title,
      relationships: card.relationships ?? [],
    }
  }
  const own = ownContextLayer(state, { scope, id: targetId })
  if (!own) throw new DomainError('invalid_reference', `The finding’s target ${scope} is not on this board`)
  return { own, parents: parentContextLayers(state, { scope, id: targetId }), title: own.source.title, relationships: null }
}

export interface ProposalResult {
  context: StructuredContext | null
  relationships: Relationship[] | null
}

/**
 * What accepting the proposal would write, computed against the board as it is now.
 * Throws a validation error, keyed by proposal path, when the proposal no longer fits —
 * or never did — so an agent learns at creation and a person at acceptance.
 */
export function resolveProposal(state: NormalizedBoard, finding: Pick<Finding, 'targetScope' | 'targetId' | 'proposal'>): ProposalResult {
  const target = targetState(state, finding.targetScope, finding.targetId)
  const proposal = finding.proposal
  const result: ProposalResult = { context: null, relationships: null }
  if (!proposal) return result
  const errors: Record<string, string> = {}

  if (proposal.context) {
    try {
      result.context = patchContext(target.own.context, proposal.context, target.parents)
    } catch (error) {
      if (!(error instanceof DomainError)) throw error
      for (const [key, message] of Object.entries(error.details ?? {})) errors[`context.${key}`] = message
    }
  }

  if (proposal.addRelationships && proposal.addRelationships.length > 0) {
    if (finding.targetScope !== 'card' || !target.relationships) {
      errors.addRelationships = 'Relationships can only be proposed for a card.'
    } else {
      const next = [...target.relationships]
      proposal.addRelationships.forEach((proposed, index) => {
        if (proposed.targetId === finding.targetId) {
          errors[`addRelationships.${index}`] = 'A card cannot relate to itself.'
        } else if (!state.cards[proposed.targetId]) {
          errors[`addRelationships.${index}`] = 'The related card is not on this board.'
        } else if (next.some((item) => item.type === proposed.type && item.targetId === proposed.targetId)) {
          errors[`addRelationships.${index}`] = 'That relationship already exists.'
        } else {
          next.push({ ...proposed, id: newId() })
        }
      })
      result.relationships = next
    }
  }

  if (Object.keys(errors).length > 0) {
    throw new DomainError('validation', 'The finding’s proposal does not fit the current board.', errors)
  }
  return result
}

/** Validates a finding before it is created: its target, and that any proposal applies. */
export function validateFinding(state: NormalizedBoard, finding: FindingCreate): void {
  const target = targetState(state, finding.targetScope, finding.targetId)
  if (!findingHasProposal(finding)) return
  const resolved = resolveProposal(state, finding)
  const current = structuredContextSchema.parse(target.own.context ?? {})
  const sameContext = resolved.context === null || JSON.stringify(resolved.context) === JSON.stringify(current)
  if (sameContext && resolved.relationships === null) {
    throw new DomainError('validation', 'The proposal matches what the target already states.', {
      proposal: 'Nothing would change; report a note instead, or leave it out.',
    })
  }
}

/** The target's title, or null when it is no longer on the board. */
export function findingTargetTitle(state: NormalizedBoard, finding: Pick<Finding, 'targetScope' | 'targetId'>): string | null {
  try {
    return targetState(state, finding.targetScope, finding.targetId).title
  } catch {
    return null
  }
}
